import { gql } from "utils/urql";
import { AuthorBioFragment, ArticleBioFragment } from "types/api";
import Image from "components/Image";
import FacebookIcon from "assets/social-icons/facebook.svg";
import TwitterIcon from "assets/social-icons/twitter.svg";
import EmailIcon from "assets/social-icons/email.svg";
import styles from "./ArticleBio.module.scss";

const AuthorSocialMedia = ({ displayName, socialMedia }: AuthorBioFragment) => {
  const facebook = socialMedia.find((social) => social.platform === "FACEBOOK");
  const twitter = socialMedia.find((social) => social.platform === "TWITTER");
  const email = socialMedia.find((social) => social.platform === "EMAIL");
  return (
    <div className={styles.social}>
      <span className={styles.connect}>Connect</span>

      {facebook && (
        <a
          className={styles.socialLink}
          href={facebook.url}
          target="_blank"
          rel="noreferrer"
        >
          <FacebookIcon
            title={`${displayName}'s Facebook Profile`}
            className={styles.socialLinkIcon}
          />
          <span className={styles.socialLinkName}>Facebook</span>
        </a>
      )}
      {twitter && (
        <a
          className={styles.socialLink}
          href={twitter.url}
          target="_blank"
          rel="noreferrer"
        >
          <TwitterIcon
            title={`${displayName}'s Twitter Profile`}
            className={styles.socialLinkIcon}
          />
          <span className={styles.socialLinkName}>Twitter</span>
        </a>
      )}
      {email && (
        <a className={styles.socialLink} href={`mailto:${email.url}`}>
          <EmailIcon
            title={`Email ${displayName}`}
            className={styles.socialLinkIcon}
          />
        </a>
      )}
    </div>
  );
};
const ArticleBio = ({ authors }: ArticleBioFragment) => {
  return (
    <section>
      {authors.map((author, index) => {
        return (
          <address
            key={author.id}
            id={`article-writer-${index}`}
            className={styles.root}
          >
            {author.headshot && (
              <div className={styles.imgContainer}>
                <figure className={styles.media}>
                  <a
                    href={author.url}
                    title={`${author.displayName}'s writer page`}
                    data-action="click author - image"
                    data-label={author.url}
                  >
                    <Image
                      lazy={true}
                      width={author.headshot.width}
                      height={author.headshot.height}
                      className={styles.img}
                      src={author.headshot.url}
                      srcSet={author.headshot.srcSet || ""}
                      alt={author.headshot.altText || ""}
                    />
                  </a>
                </figure>
              </div>
            )}
            <div className={styles.content}>
              {author.biography?.default && (
                <div
                  className={styles.bio}
                  dangerouslySetInnerHTML={{ __html: author.biography.default }}
                />
              )}
              {author.socialMedia.length > 0 && (
                <AuthorSocialMedia {...author} />
              )}
            </div>
          </address>
        );
      })}
    </section>
  );
};

ArticleBio.fragment = gql`
  fragment authorBio on Author {
    id
    displayName
    url
    headshot(width: 100, height: 100, sizes: ["1x", "2x"]) {
      width
      height
      altText
      srcSet
      url
    }
    biography {
      default
    }
    socialMedia {
      platform
      url
    }
  }
  fragment articleBio on Article {
    authors {
      ...authorBio
    }
  }
`;

export default ArticleBio;
